import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { usePostHog } from '@posthog/react';
import { ArrowUpRight, ChevronDown, Factory, FolderOpen, Layers, Sparkles, Trash2, Upload } from 'lucide-react';
import Tooltip from '@mui/material/Tooltip';
import { AppIcon } from './LucideIcon';
import { DataTable, PageHeader, StatusFilterBar, TabNavigation } from '@ravenappdev/raven-ui';
import { documentApi, extractionApi, pnidApi } from '../api';
import PnidModal from './PnidModal';
import { uploadDrawing } from '../utils/uploadDrawing';
import './pnid-file-manager.css';
import { 
  PidDocument, 
  AnnotationInfo,
  ExtractionJob, 
  PnidAIData,
  BackgroundExtractionJob,
} from '../types';
import DocumentViewer from './DocumentViewer';
import { useAuth } from '../contexts/AuthContext';
import {
  type DiagramTypeFilter,
  documentMatchesSearch,
  getDocumentDiagramType,
} from '../utils/documentLabels';
import {
  ALL_DOMAINS_SCOPE,
  UNASSIGNED_DOMAIN,
  buildChildDomainTabs,
  buildTopDomainTabs,
  documentInScope,
  sameScope,
  topKeyForScope,
  type DomainScope,
} from '../utils/domainTree';
import { useDomainIndex } from '../hooks/useDomainIndex';
import usePlantScope from './product/usePlantScope';

interface PnidFileManagerProps {
  isOpen: boolean;
  onClose: () => void;
  onDocumentReady?: (docId: string) => void;
  /**
   * 'modal' is the drawing workspace's rail button. 'page' is the File Manager
   * rail destination, which owns a whole route and so takes the shell's
   * PageHeader instead of a dialog surface. The body is the same either way.
   */
  presentation?: 'modal' | 'page';
}

type FileStatus = 'idle' | 'uploading' | 'processing' | 'completed' | 'failed';

interface ManagedFile {
  id: string;
  name: string;
  docId?: string;
  jobId?: string;
  status: FileStatus;
  progress?: string;
  error?: string;
  pollingPaused?: boolean;
  aiData?: PnidAIData;
}

const BACKGROUND_POLL_INTERVAL_MS = 60000;

function normalizeIdentity(value: unknown): string {
  return String(value ?? '').trim().toLowerCase();
}

export const PnidFileManager: React.FC<PnidFileManagerProps> = ({
  isOpen,
  onClose,
  onDocumentReady,
  presentation = 'modal',
}) => {
  const { user } = useAuth();
  const posthog = usePostHog();
  const [files, setFiles] = useState<ManagedFile[]>([]);
  const [documents, setDocuments] = useState<PidDocument[]>([]);
  const [annotations, setAnnotations] = useState<Record<string, AnnotationInfo>>({});
  const [dragOver, setDragOver] = useState(false);
  type FileManagerTab = 'upload' | 'existing' | 'background';
  const [activeTab, setActiveTab] = useState<FileManagerTab>('upload');
  const [backgroundJobs, setBackgroundJobs] = useState<BackgroundExtractionJob[]>([]);
  const [backgroundLoading, setBackgroundLoading] = useState(false);
  const [backgroundError, setBackgroundError] = useState<string | null>(null);

  // Document viewer state
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerDocId, setViewerDocId] = useState<string>('');
  const [viewerDocName, setViewerDocName] = useState<string>('');

  // AI data upload state
  const [aiUploadingFor, setAiUploadingFor] = useState<string | null>(null);
  const [archivingDocId, setArchivingDocId] = useState<string | null>(null);
  const [archiveError, setArchiveError] = useState<string | null>(null);

  // User and workflow state
  const [userName, setUserName] = useState<string>(() => {
    return localStorage.getItem('pnid_user_name') || '';
  });
  const { index: domainIndex } = useDomainIndex();
  const [showUserPrompt, setShowUserPrompt] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeDomainScope, setActiveDomainScope] = useState<DomainScope>(ALL_DOMAINS_SCOPE);
  const [activeDiagramFilter, setActiveDiagramFilter] = useState<DiagramTypeFilter>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const effectiveUserName = user?.username || userName;
  const isAdmin = effectiveUserName === 'raven_admin';
  const canArchiveDocuments = normalizeIdentity(effectiveUserName) === 'sme_demo';

  // Check if user name is set on mount
  useEffect(() => {
    if (!effectiveUserName && isOpen) {
      setShowUserPrompt(true);
    }
  }, [effectiveUserName, isOpen]);

  const handleSetUserName = (name: string) => {
    const trimmed = name.trim();
    if (trimmed) {
      setUserName(trimmed);
      localStorage.setItem('pnid_user_name', trimmed);
      setShowUserPrompt(false);
    }
  };

  useEffect(() => {
    if (!user?.username || userName === user.username) return;
    localStorage.setItem('pnid_user_name', user.username);
    setUserName(user.username);
  }, [user?.username, userName]);

  /* Memoised because the status counts derive from it: an identity that changed
     every render would recompute that memo on every render too. */
  const getAnnotation = useCallback(
    (docId: string): AnnotationInfo | null => annotations[docId] || null,
    [annotations],
  );

  const handleAssignDocument = async (docId: string, user: string) => {
    try {
      await documentApi.assignDocument(docId, user);
      await loadDocumentsAndAnnotations();
    } catch (error) {
      console.error('Failed to assign document:', error);
    }
  };

  const handleUpdateStatus = async (docId: string, status: string) => {
    try {
      await documentApi.updateStatus(docId, status);
      const doc = documents.find((d) => d.id === docId);
      const baseProps = {
        diagram_id: docId,
        diagram_name: doc?.original_filename ?? null,
        status,
      };
      posthog?.capture('annotation_status_updated', baseProps);
      if (status === 'needs_review') {
        posthog?.capture('marked_for_qc', baseProps);
      }
      await loadDocumentsAndAnnotations();
    } catch (error) {
      console.error('Failed to update status:', error);
    }
  };

  /* The rail's plant switcher scopes this page too. Without it a person who had
     picked Line-1F still saw all 869 documents here, every other project listed
     in the filter pane, and counts drawn from the whole account — while Home,
     one rail item above, showed them their plant. Drawings only ever carry a
     LEAF plant name, so the switcher records the whole family and the scope is
     matched against that set: the same rule the drawings library follows. Every
     count and list below is drawn from this pool, so the pane reports what the
     scope actually holds. */
  const plantScope = usePlantScope();
  const scopedDocuments = useMemo(() => {
    /* The domain id first: `documentInScope` resolves a parent through the
       domain index, so scoping to Ammonia catches Train-1 and Train-2. The
       stored family cannot be relied on for that — it is only written when
       someone picks a plant through the switcher, so an older scope carries the
       bare name and a parent would then match nothing at all. The family is the
       fallback for a scope recorded without an id. */
    if (plantScope.domainId) {
      const scope: DomainScope = { kind: 'subtree', domainId: plantScope.domainId };
      return documents.filter((doc) => documentInScope(domainIndex, doc, scope));
    }
    if (!plantScope.names.length) return documents;
    const family = new Set(plantScope.names);
    return documents.filter((doc) => family.has(doc.plant || ''));
  }, [documents, domainIndex, plantScope.domainId, plantScope.names]);

  const diagramTabs = useMemo(() => {
    const counts: Record<DiagramTypeFilter, number> = { all: scopedDocuments.length, pid: 0, pfd: 0 };
    for (const doc of scopedDocuments) {
      counts[getDocumentDiagramType(doc)] += 1;
    }
    return [
      { key: 'all' as const, label: 'All', count: counts.all },
      { key: 'pid' as const, label: 'P&ID', count: counts.pid },
      { key: 'pfd' as const, label: 'PFD', count: counts.pfd },
    ].filter((tab) => tab.key === 'all' || tab.count > 0);
  }, [scopedDocuments]);

  const diagramScopedDocuments = useMemo(
    () => scopedDocuments.filter(
      (doc) => activeDiagramFilter === 'all' || getDocumentDiagramType(doc) === activeDiagramFilter,
    ),
    [activeDiagramFilter, scopedDocuments],
  );

  const topDomainTabs = useMemo(
    () => buildTopDomainTabs(domainIndex, diagramScopedDocuments),
    [domainIndex, diagramScopedDocuments],
  );

  const activeTopKey = topKeyForScope(domainIndex, activeDomainScope);

  const childDomainTabs = useMemo(() => {
    if (activeTopKey === 'all' || activeTopKey === UNASSIGNED_DOMAIN) return [];
    return buildChildDomainTabs(domainIndex, diagramScopedDocuments, activeTopKey);
  }, [domainIndex, diagramScopedDocuments, activeTopKey]);

  useEffect(() => {
    if (!diagramTabs.some((tab) => tab.key === activeDiagramFilter)) {
      setActiveDiagramFilter('all');
    }
  }, [activeDiagramFilter, diagramTabs]);

  useEffect(() => {
    if (activeDomainScope.kind === 'all') return;
    if (!topDomainTabs.some((tab) => tab.key === activeTopKey)) {
      setActiveDomainScope(ALL_DOMAINS_SCOPE);
    }
  }, [activeDomainScope, activeTopKey, topDomainTabs]);

  // Filter structurally first, then search/status; show all group docs within scope.
  const filteredDocuments = scopedDocuments.filter(doc => {
    const annotation = getAnnotation(doc.id);
    if (activeDiagramFilter !== 'all' && getDocumentDiagramType(doc) !== activeDiagramFilter) {
      return false;
    }
    if (!documentInScope(domainIndex, doc, activeDomainScope)) {
      return false;
    }
    const matchesSearch = documentMatchesSearch(doc, annotation, searchQuery);
    const docStatus = annotation?.status || 'draft';
    const matchesStatus = statusFilter === 'all' || docStatus === statusFilter;
    return matchesSearch && matchesStatus;
  });

  /* Status counts, so every row in the filter pane carries a number. They
     honour the other three filters but not status itself, which is how a
     filter list reports "what would I get if I picked this instead". */
  const statusCounts = useMemo(() => {
    const counts: Record<string, number> = {
      all: 0, draft: 0, in_progress: 0, completed: 0, needs_review: 0,
    };
    scopedDocuments.forEach((doc) => {
      const annotation = getAnnotation(doc.id);
      if (activeDiagramFilter !== 'all' && getDocumentDiagramType(doc) !== activeDiagramFilter) return;
      if (!documentInScope(domainIndex, doc, activeDomainScope)) return;
      if (!documentMatchesSearch(doc, annotation, searchQuery)) return;
      counts.all += 1;
      const docStatus = annotation?.status || 'draft';
      if (docStatus in counts) counts[docStatus] += 1;
    });
    return counts;
  }, [scopedDocuments, getAnnotation, activeDiagramFilter, domainIndex, activeDomainScope, searchQuery]);

  /* One row object per document. The handlers stay in this component, so the
     render callbacks close over `doc` via a lookup rather than stuffing
     functions into the row. */
  const docsById = useMemo(
    () => new Map(documents.map((doc) => [doc.id, doc])),
    [documents],
  );

  type FileRow = {
    id: string;
    name: string;
    dimensions: string;
    uploaded: string;
    assigned: string;
    status: string;
    /* Its own key: two columns sharing 'id' meant the second one's width was
       dropped, and Actions wrapped to three lines at the leftover width. */
    actions: string;
  };

  /* Each filter group collapses. A reviewer who has settled on a project wants
     that section out of the way without losing what it is set to — so the
     header keeps its label. Status is not here; it sits over the table. */
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({
    type: true, project: true,
  });
  const toggleGroup = (key: string) =>
    setOpenGroups((current) => ({ ...current, [key]: !current[key] }));

  /* The document awaiting a delete confirmation, or null. */
  const [pendingDelete, setPendingDelete] = useState<PidDocument | null>(null);

  const [filePage, setFilePage] = useState(0);
  const [fileRowsPerPage, setFileRowsPerPage] = useState(25);

  /* Slice to the page, as the library does. DataTable paginates the control
     strip, not the rows it is handed — passing all 869 rendered all 869. */
  const fileRows: FileRow[] = filteredDocuments
    .slice(filePage * fileRowsPerPage, filePage * fileRowsPerPage + fileRowsPerPage)
    .map((doc) => {
    const annotation = getAnnotation(doc.id);
    const uploaded = new Date(doc.uploaded_at);
    const pad = (v: number) => String(v).padStart(2, '0');
    return {
      id: doc.id,
      name: doc.original_filename,
      dimensions: doc.width && doc.height ? `${doc.width}×${doc.height}` : '—',
      /* dd/mm/yyyy, the format the drawings library settled on. */
      uploaded: Number.isNaN(uploaded.getTime())
        ? '—'
        : `${pad(uploaded.getDate())}/${pad(uploaded.getMonth() + 1)}/${uploaded.getFullYear()}`,
      assigned: annotation?.user || 'Unassigned',
      status: annotation?.status || 'draft',
      actions: '',
      };
    });

  type FileColumn = {
    key: keyof FileRow;
    label: string;
    width?: number | string;
    render?: (value: FileRow[keyof FileRow], row: FileRow) => React.ReactNode;
  };

  const fileColumns: FileColumn[] = [
    { key: 'name', label: 'Document', width: 320 },
    { key: 'id', label: 'ID', width: 260, render: (value) => (
      <span className="pnid-fm-cell-id">{String(value)}</span>
    ) },
    { key: 'dimensions', label: 'Size', width: 110 },
    { key: 'uploaded', label: 'Uploaded', width: 110 },
    { key: 'assigned', label: 'Assigned', width: 150 },
    {
      key: 'status',
      label: 'Status',
      width: 170,
      /* The select IS the status — the card showed a chip beside a picker that
         said the same thing. An unassigned document has nothing to set yet, so
         it offers the assignment instead. */
      render: (value, row) => (isAdmin && row.assigned === 'Unassigned' ? (
        <button
          type="button"
          className="pnid-btn pnid-btn--outlined pnid-btn--sm"
          onClick={() => {
            const name = prompt('Assign to user (enter name):');
            if (name) {
              handleAssignDocument(row.id, name);
              handleUpdateStatus(row.id, 'in_progress');
            }
          }}
        >
          Assign
        </button>
      ) : (
        <select
          className="pnid-field pnid-fm-select"
          value={String(value)}
          aria-label={`Status for ${row.name}`}
          onChange={(e) => handleUpdateStatus(row.id, e.target.value)}
        >
          <option value="draft">Draft</option>
          <option value="in_progress">In Progress</option>
          <option value="completed">Completed</option>
          <option value="needs_review">Needs Review</option>
        </select>
      )),
    },
    {
      key: 'actions',
      label: 'Actions',
      /* Measured: Files+Open+Delete need 232px on one line, and an admin's two
         extra buttons take it to ~420. Sized so the row never wraps to three
         lines the way the 170px guess did. */
      width: isAdmin ? 420 : 240,
      render: (_value, row) => {
        const doc = docsById.get(row.id);
        if (!doc) return null;
        const uploadTitle = aiUploadingFor === row.id ? 'Uploading AI data' : 'Upload AI data JSON';
        return (
          <div className="pnid-fm-cell-actions">
            <Tooltip title="View all files">
              <button
                type="button"
                className="pnid-fm-iconbtn"
                onClick={() => handleViewDocument(doc)}
                aria-label={`View all files for ${row.name}`}
              >
                <AppIcon icon={FolderOpen} size={20} />
              </button>
            </Tooltip>
            {isAdmin && (
              <>
                <Tooltip title="Start AI extraction">
                  <button
                    type="button"
                    className="pnid-fm-iconbtn"
                    onClick={() => handleStartAIExtraction(row.id)}
                    aria-label={`Start AI extraction for ${row.name}`}
                  >
                    <AppIcon icon={Sparkles} size={20} />
                  </button>
                </Tooltip>
                <input
                  type="file"
                  accept=".json"
                  onChange={(e) => handleAiDataUpload(row.id, e)}
                  style={{ display: 'none' }}
                  id={`ai-upload-${row.id}`}
                  disabled={aiUploadingFor === row.id}
                />
                <Tooltip title={uploadTitle}>
                  <label
                    htmlFor={`ai-upload-${row.id}`}
                    className="pnid-fm-iconbtn"
                    aria-label={`Upload AI data JSON for ${row.name}`}
                  >
                    <AppIcon icon={Upload} size={20} />
                  </label>
                </Tooltip>
              </>
            )}
            <Tooltip title="Open document">
              <button
                type="button"
                className="pnid-fm-iconbtn pnid-fm-iconbtn--primary"
                onClick={() => handleOpenDocument(row.id)}
                aria-label={`Open ${row.name}`}
              >
                <AppIcon icon={ArrowUpRight} size={20} />
              </button>
            </Tooltip>
            {canArchiveDocuments && (
              /* Wrapped in a span so the tooltip still fires while the button is
                 disabled mid-archive — MUI drops pointer events on a bare
                 disabled child. */
              <Tooltip title="Delete document">
                <span className="pnid-fm-iconbtn-wrap">
                  <button
                    type="button"
                    className="pnid-fm-iconbtn pnid-fm-iconbtn--danger"
                    onClick={() => setPendingDelete(doc)}
                    disabled={archivingDocId === row.id}
                    aria-label={`Delete ${row.name}`}
                  >
                    <AppIcon icon={Trash2} size={20} />
                  </button>
                </span>
              </Tooltip>
            )}
          </div>
        );
      },
    },
  ];

  const STATUS_ROWS: Array<{ key: string; label: string }> = [
    { key: 'all', label: 'All' },
    { key: 'draft', label: 'Draft' },
    { key: 'in_progress', label: 'In Progress' },
    { key: 'completed', label: 'Completed' },
    { key: 'needs_review', label: 'Needs Review' },
  ];

  // Count of unassigned documents (for admin)
  const unassignedCount = scopedDocuments.filter(doc => !getAnnotation(doc.id)?.user).length;

  /* No status chip here. Home is a read-only library, so a chip is the right
     display there; this page exists to change status, and the select already
     shows the current value. A chip beside a picker of the same fact was the
     card layout stating it twice. */

  const loadDocuments = async () => {
    try {
      const docs = await documentApi.list();
      setDocuments(docs);
    } catch (error) {
      console.error('Failed to load documents:', error);
    }
  };

  const loadAnnotations = async () => {
    try {
      // Load all annotations in the user's group so we can show all group P&IDs and assignment info
      const annotationList = await documentApi.listAnnotations(undefined, isAdmin);
      const annotationMap: Record<string, AnnotationInfo> = {};
      for (const ann of annotationList) {
        annotationMap[ann.id] = ann;
      }
      setAnnotations(annotationMap);
    } catch (error) {
      console.error('Failed to load annotations:', error);
    }
  };

  const loadDocumentsAndAnnotations = async () => {
    await Promise.all([loadDocuments(), loadAnnotations()]);
  };

  const loadBackgroundJobs = useCallback(async (showLoading: boolean = true) => {
    if (showLoading) {
      setBackgroundLoading(true);
    }
    setBackgroundError(null);
    try {
      const result = await extractionApi.listBackgroundJobs();
      setBackgroundJobs(result.jobs);
    } catch (error) {
      console.error('Failed to load background jobs:', error);
      setBackgroundError('Failed to load background jobs.');
    } finally {
      if (showLoading) {
        setBackgroundLoading(false);
      }
    }
  }, []);

  const handlePollBackgroundJobs = useCallback(async (showLoading: boolean = true) => {
    if (showLoading) {
      setBackgroundLoading(true);
    }
    setBackgroundError(null);
    try {
      const result = await extractionApi.pollBackgroundJobs();
      setBackgroundJobs(result.jobs);
    } catch (error) {
      console.error('Failed to poll background jobs:', error);
      setBackgroundError('Failed to poll background jobs.');
    } finally {
      if (showLoading) {
        setBackgroundLoading(false);
      }
    }
  }, []);

  const handleCancelBackgroundJob = async (jobId: string) => {
    try {
      await extractionApi.cancel(jobId);
      await loadBackgroundJobs();
    } catch (error) {
      console.error('Failed to cancel background job:', error);
    }
  };

  const handleViewDocument = (doc: PidDocument) => {
    setViewerDocId(doc.id);
    setViewerDocName(doc.original_filename);
    setViewerOpen(true);
  };

  const handleStartAIExtraction = async (docId: string) => {
    try {
      const result = await extractionApi.startForDocument(docId);
      // Add to files list for tracking
      const doc = documents.find(d => d.id === docId);
      const fileId = crypto.randomUUID();
      setFiles(prev => [...prev, {
        id: fileId,
        name: doc?.original_filename || 'Document',
        docId,
        jobId: result.job_id || undefined,
        status: 'processing',
        progress: 'AI extraction started...',
      }]);
      if (result.job_id) {
        await pollExtractionJob(result.job_id, fileId, docId);
      }
    } catch (error: any) {
      console.error('Failed to start AI extraction:', error);
    }
  };

  const handleAiDataUpload = async (docId: string, e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setAiUploadingFor(docId);
    try {
      await extractionApi.uploadAiData(docId, file);
      // Reload documents to reflect changes
      await loadDocumentsAndAnnotations();
    } catch (error: any) {
      console.error('Failed to upload AI data:', error);
    } finally {
      setAiUploadingFor(null);
      e.target.value = '';
    }
  };

  // Load existing documents and annotations
  useEffect(() => {
    if (isOpen && effectiveUserName) {
      loadDocumentsAndAnnotations();
    }
  }, [effectiveUserName, isAdmin, isOpen]);

  useEffect(() => {
    if (isOpen && activeTab === 'background') {
      loadBackgroundJobs();
      handlePollBackgroundJobs(false);
    }
  }, [isOpen, activeTab, handlePollBackgroundJobs, loadBackgroundJobs]);

  useEffect(() => {
    if (!isOpen || activeTab !== 'background') return;
    const intervalId = window.setInterval(() => {
      handlePollBackgroundJobs(false);
    }, BACKGROUND_POLL_INTERVAL_MS);
    return () => window.clearInterval(intervalId);
  }, [activeTab, handlePollBackgroundJobs, isOpen]);

  const pollExtractionJob = async (jobId: string, fileId: string, docId: string) => {
    setFiles(prev => prev.map(f =>
      f.id === fileId ? { ...f, status: 'processing', pollingPaused: false } : f
    ));

    const finalStatus = await extractionApi.pollUntilComplete(
      jobId,
      (status) => {
        const s = status as ExtractionJob;
        setFiles(prev => prev.map(f =>
          f.id === fileId ? {
            ...f,
            progress: s.status === 'processing' ? 'AI is analyzing the P&ID...' : 'Queued...'
          } : f
        ));
      }
    );

    const job = finalStatus as ExtractionJob;
    if (job.timed_out) {
      setFiles(prev => prev.map(f =>
        f.id === fileId ? {
          ...f,
          status: 'processing',
          pollingPaused: true,
          progress: 'Polling paused. Click Resume to continue.'
        } : f
      ));
      return;
    }

    if (job.status === 'completed') {
      let aiData: PnidAIData | undefined;
      try {
        aiData = await pnidApi.getAIData(docId);
      } catch (e) {
        console.warn('Could not fetch AI data:', e);
      }

      setFiles(prev => prev.map(f =>
        f.id === fileId ? {
          ...f,
          status: 'completed' as FileStatus,
          progress: undefined,
          aiData,
          pollingPaused: false,
        } : f
      ));

      loadDocumentsAndAnnotations();
    } else {
      setFiles(prev => prev.map(f =>
        f.id === fileId ? {
          ...f,
          status: 'failed' as FileStatus,
          error: job.error_message || 'Extraction failed',
          pollingPaused: false,
        } : f
      ));
    }
  };

  const handleFiles = useCallback(async (fileList: FileList) => {
    const validFiles: { file: File; managed: ManagedFile }[] = [];
    
    for (let i = 0; i < fileList.length; i++) {
      const file = fileList[i];
      const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
      const isImage = file.type.startsWith('image/');
      
      if (!isPdf && !isImage) continue;
      
      validFiles.push({
        file,
        managed: {
          id: crypto.randomUUID(),
          name: file.name,
          status: 'idle',
        }
      });
    }
    
    if (validFiles.length === 0) return;
    
    setFiles(prev => [...prev, ...validFiles.map(v => v.managed)]);
    
    // Process each valid file
    for (const { file, managed } of validFiles) {
      await processFile(file, managed.id);
    }
  }, []);

  const processFile = async (file: File, fileId: string) => {
    // Update status to uploading
    setFiles(prev => prev.map(f => 
      f.id === fileId ? { ...f, status: 'uploading' as FileStatus, progress: 'Uploading...' } : f
    ));

    try {
      /* The dropzone has always advertised "PDF or image files" and
         `handleFiles` has always let images through, but this posted every one
         of them to /pnid-ai/upload, which is PDF-only and answers 400 "Only PDF
         files are allowed for AI extraction". Dropping a PNG here simply
         failed. `uploadDrawing` sends each file to the endpoint that takes it. */
      const { docId, jobId } = await uploadDrawing(file);

      if (!jobId) {
        /* An image goes through /upload, which stores the drawing and starts no
           extraction — there is no job to poll, so the file is done. */
        setFiles(prev => prev.map(f =>
          f.id === fileId ? { ...f, docId, status: 'completed' as FileStatus, progress: undefined } : f
        ));
        await loadDocumentsAndAnnotations();
        return;
      }

      setFiles(prev => prev.map(f => 
        f.id === fileId ? { 
          ...f, 
          docId,
          jobId,
          status: 'processing' as FileStatus, 
          progress: 'AI extraction in progress...',
          pollingPaused: false,
        } : f
      ));

      // Poll for completion
      await pollExtractionJob(jobId, fileId, docId);
    } catch (error: any) {
      setFiles(prev => prev.map(f => 
        f.id === fileId ? { 
          ...f, 
          status: 'failed' as FileStatus, 
          error: error.message || 'Upload failed',
          pollingPaused: false,
        } : f
      ));
    }
  };

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files.length > 0) {
      handleFiles(e.dataTransfer.files);
    }
  }, [handleFiles]);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFiles(e.target.files);
    }
  }, [handleFiles]);

  const handleOpenDocument = (docId: string) => {
    onDocumentReady?.(docId);
    onClose();
  };

  /* Deleting always asks first, in the product's own dialog. window.confirm
     blocks the page, cannot be styled, says "localhost says", and browsers let
     a user tick "don't ask again" — a suppressible prompt in front of a
     destructive action is no prompt at all. */
  const handleArchiveDocument = async (doc: PidDocument) => {
    setArchivingDocId(doc.id);
    setArchiveError(null);
    try {
      await documentApi.archive(doc.id);
      posthog?.capture('document_archived', {
        diagram_id: doc.id,
        diagram_name: doc.original_filename,
      });
      await loadDocumentsAndAnnotations();
    } catch (error: any) {
      console.error('Failed to archive document:', error);
      setArchiveError(error.response?.data?.detail || error.message || 'Failed to archive document.');
    } finally {
      setArchivingDocId(null);
    }
  };

  const handleRemoveFile = (fileId: string) => {
    setFiles(prev => prev.filter(f => f.id !== fileId));
  };

  const handleCancelJob = async (fileId: string) => {
    const file = files.find(f => f.id === fileId);
    if (!file?.jobId) return;

    try {
      await extractionApi.cancel(file.jobId);
      setFiles(prev => prev.map(f => 
        f.id === fileId ? { ...f, status: 'failed', error: 'Cancelled', pollingPaused: false } : f
      ));
    } catch (e) {
      console.error('Failed to cancel:', e);
    }
  };

  const handleResumePolling = async (fileId: string) => {
    const file = files.find(f => f.id === fileId);
    if (!file?.jobId || !file.docId) return;
    setFiles(prev => prev.map(f =>
      f.id === fileId ? { ...f, progress: 'Resuming polling...', pollingPaused: false } : f
    ));
    await pollExtractionJob(file.jobId, fileId, file.docId);
  };

  const formatTimestamp = (value?: string | null) => {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleString();
  };

  /* Uploading is admin-only, so the tab strip is too — but `activeTab` still
     starts at 'upload'. A non-admin therefore opened onto the upload pane with
     NEITHER visible tab selected, and could never get back to it after clicking
     away. The available tabs are the source of truth now, and anything not in
     them falls back to the first. */
  const TAB_LABELS: Record<FileManagerTab, string> = {
    upload: 'Upload New',
    existing: 'Existing Documents',
    background: 'Background Jobs',
  };
  const availableTabs: FileManagerTab[] = isAdmin
    ? ['upload', 'existing', 'background']
    : ['existing', 'background'];
  const activeTabIndex = Math.max(0, availableTabs.indexOf(activeTab));
  const resolvedTab = availableTabs[activeTabIndex];

  if (!isOpen && presentation === 'modal') return null;

  const deleteConfirm = pendingDelete && (
    <PnidModal
      open
      title="Delete Document"
      onClose={() => setPendingDelete(null)}
      footer={(
        <>
          <button
            type="button"
            className="pnid-btn pnid-btn--sm pnid-btn--outlined"
            onClick={() => setPendingDelete(null)}
          >
            Cancel
          </button>
          <button
            type="button"
            className="pnid-btn pnid-btn--sm pnid-btn--contained"
            onClick={() => {
              const doc = pendingDelete;
              setPendingDelete(null);
              handleArchiveDocument(doc);
            }}
          >
            Delete
          </button>
        </>
      )}
    >
      <p className="pnid-modal__hint" style={{ margin: 0 }}>
        Delete <strong>{pendingDelete.original_filename}</strong>?
      </p>
      <p className="pnid-modal__hint">
        This archives the document so it is hidden from the normal lists. It is
        not permanently deleted.
      </p>
    </PnidModal>
  );

  // User name prompt modal
  if (showUserPrompt) {
    return (
      <PnidModal open title="Welcome" onClose={() => setShowUserPrompt(false)}>
        <p className="pnid-fm-prompt-copy">Please enter your name to continue:</p>
        <input
          type="text"
          placeholder="Your name"
          className="pnid-fm-prompt-input"
          autoFocus
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              handleSetUserName((e.target as HTMLInputElement).value);
            }
          }}
        />
        <button
          type="button"
          className="pnid-btn pnid-btn--contained"
          style={{ width: '100%' }}
          onClick={(e) => {
            const input = (e.currentTarget.previousElementSibling as HTMLInputElement);
            handleSetUserName(input.value);
          }}
        >
          Continue
        </button>
      </PnidModal>
    );
  }

  const body = (
    <>
        {deleteConfirm}
        {/* No "Working as" bar: the rail's account block already names the
            signed-in user on every screen, and My Profile in the avatar menu
            changes it. The first-run prompt below still covers the case where
            no name is set yet. */}

        <div className="pnid-fm-tabs">
          <TabNavigation
            appearance="underline"
            ariaLabel="File manager sections"
            tabs={availableTabs.map((key) => ({ label: TAB_LABELS[key] }))}
            activeIndex={activeTabIndex}
            onChange={(index) => setActiveTab(availableTabs[index])}
          />
        </div>

        <div className="pnid-fm-pane">
          {resolvedTab === 'upload' && (
            <>
              <div
                className={`pnid-fm-dropzone${dragOver ? ' pnid-fm-dropzone--active' : ''}`}
                onDrop={handleDrop}
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
              >
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png,image/*,application/pdf"
                  onChange={handleInputChange}
                  multiple
                  className="pnid-fm-hidden-input"
                  id="file-manager-upload"
                />
                <label htmlFor="file-manager-upload" className="pnid-fm-dropzone-label">
                  <svg className="pnid-fm-upload-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="17 8 12 3 7 8" />
                    <line x1="12" y1="3" x2="12" y2="15" />
                  </svg>
                  <span><strong>Click to upload</strong> or drag and drop</span>
                  <span className="pnid-fm-hint">PDF or image files • Multiple files supported</span>
                </label>
              </div>

              {files.length > 0 && (
                <div className="pnid-fm-list pnid-fm-list--gap-sm">
                  <h4 className="pnid-fm-list-title">Processing Queue</h4>
                  {files.map(file => (
                    <div key={file.id} className="pnid-fm-row pnid-fm-row--stack">
                      <div className="pnid-fm-row-head">
                        <span className="pnid-fm-row-name">{file.name}</span>
                        <span className="pnid-fm-row-status">
                          {file.status === 'uploading' && '⏳ ' + (file.progress || 'Uploading...')}
                          {file.status === 'processing' && '🔄 ' + (file.progress || 'Processing...')}
                          {file.status === 'completed' && '✅ Complete'}
                          {file.status === 'failed' && '❌ ' + (file.error || 'Failed')}
                        </span>
                      </div>

                      {file.status === 'completed' && file.aiData && (
                        <div className="pnid-fm-ai-summary">
                          <span>{file.aiData.equipment_in_scope?.length || 0} equipment</span>
                          <span>•</span>
                          <span>{file.aiData.instruments?.length || 0} instruments</span>
                          <span>•</span>
                          <span>{file.aiData.lines?.length || 0} lines</span>
                        </div>
                      )}

                      <div className="pnid-fm-row-actions">
                        {file.status === 'completed' && file.docId && (
                          <button
                            className="pnid-btn pnid-btn--contained pnid-btn--sm"
                            onClick={() => handleOpenDocument(file.docId!)}
                          >
                            Open
                          </button>
                        )}
                        {file.status === 'processing' && (
                          <>
                            {file.pollingPaused && (
                              <button
                                className="pnid-btn pnid-btn--outlined pnid-btn--sm"
                                onClick={() => handleResumePolling(file.id)}
                              >
                                Resume
                              </button>
                            )}
                            <button
                              className="pnid-btn pnid-btn--outlined pnid-btn--sm"
                              onClick={() => handleCancelJob(file.id)}
                            >
                              Cancel
                            </button>
                          </>
                        )}
                        {(file.status === 'completed' || file.status === 'failed') && (
                          <button
                            className="pnid-btn pnid-btn--outlined pnid-btn--sm"
                            onClick={() => handleRemoveFile(file.id)}
                          >
                            ×
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {resolvedTab === 'existing' && (
            <div className="pnid-fm-list">
              {/* Filters live in a pane beside the results, not in a band above
                  them: four groups of chips wrapped to five rows and pushed the
                  documents below the fold. A vertical list also gives every
                  option a fixed place and a right-aligned count. */}
              <aside className="pnid-fm-filterpane" aria-label="Filters">
                <input
                  type="text"
                  placeholder="Search documents..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pnid-field pnid-fm-search"
                  aria-label="Search documents"
                />

                <div className="pnid-fm-filtergroup" role="group" aria-label="Type">
                  <button
                    type="button"
                    className="pnid-fm-filtergroup-title"
                    aria-expanded={openGroups.type}
                    onClick={() => toggleGroup('type')}
                  >
                    <AppIcon icon={Layers} size={14} className="pnid-fm-filtergroup-icon" />
                    <span className="pnid-fm-filtergroup-name">Type</span>
                    <AppIcon
                      icon={ChevronDown}
                      size={14}
                      className={openGroups.type
                        ? 'pnid-fm-filtergroup-caret pnid-fm-filtergroup-caret--open'
                        : 'pnid-fm-filtergroup-caret'}
                    />
                  </button>
                  {openGroups.type && (<>
                  {diagramTabs.map((tab) => (
                    <button
                      key={tab.key}
                      type="button"
                      aria-pressed={activeDiagramFilter === tab.key}
                      className={`pnid-fm-filterrow${activeDiagramFilter === tab.key ? ' pnid-fm-filterrow--active' : ''}`}
                      onClick={() => setActiveDiagramFilter(tab.key)}
                    >
                      <span className="pnid-fm-filterrow-label">{tab.label}</span>
                      <span className="pnid-fm-filterrow-count">{tab.count}</span>
                    </button>
                  ))}
                  </>)}
                </div>

                <div className="pnid-fm-filtergroup" role="group" aria-label="Project">
                  <button
                    type="button"
                    className="pnid-fm-filtergroup-title"
                    aria-expanded={openGroups.project}
                    onClick={() => toggleGroup('project')}
                  >
                    <AppIcon icon={Factory} size={14} className="pnid-fm-filtergroup-icon" />
                    <span className="pnid-fm-filtergroup-name">Project</span>
                    <AppIcon
                      icon={ChevronDown}
                      size={14}
                      className={openGroups.project
                        ? 'pnid-fm-filtergroup-caret pnid-fm-filtergroup-caret--open'
                        : 'pnid-fm-filtergroup-caret'}
                    />
                  </button>
                  {openGroups.project && (<>
                  {topDomainTabs.map((tab) => (
                    <button
                      key={tab.key}
                      type="button"
                      aria-pressed={tab.key === activeTopKey}
                      className={`pnid-fm-filterrow${tab.key === activeTopKey ? ' pnid-fm-filterrow--active' : ''}`}
                      onClick={() => setActiveDomainScope(tab.scope)}
                    >
                      <span className="pnid-fm-filterrow-label">{tab.label}</span>
                      <span className="pnid-fm-filterrow-count">{tab.count}</span>
                    </button>
                  ))}
                  {/* Children sit under the project they belong to, indented,
                      rather than on an unlabelled row of their own. */}
                  {childDomainTabs.map((tab) => (
                    <button
                      key={tab.key}
                      type="button"
                      aria-pressed={sameScope(tab.scope, activeDomainScope)}
                      className={`pnid-fm-filterrow pnid-fm-filterrow--child${sameScope(tab.scope, activeDomainScope) ? ' pnid-fm-filterrow--active' : ''}`}
                      onClick={() => setActiveDomainScope(tab.scope)}
                    >
                      <span className="pnid-fm-filterrow-label">{tab.label}</span>
                      <span className="pnid-fm-filterrow-count">{tab.count}</span>
                    </button>
                  ))}
                  </>)}
                </div>

              </aside>

              <div className="pnid-fm-results">
                {/* Status sits over the table, not in the pane beside it. It is
                    the filter people move through most, and as a row of pills
                    it reads as the state of the results below — the same bar,
                    and the same component, Home carries over its own table. */}
                <div className="pnid-fm-statusbar">
                  <StatusFilterBar
                    filters={STATUS_ROWS.map((row) => ({
                      label: row.label,
                      count: statusCounts[row.key] ?? 0,
                    }))}
                    activeIndex={Math.max(0, STATUS_ROWS.findIndex((row) => row.key === statusFilter))}
                    onChange={(index) => setStatusFilter(STATUS_ROWS[index]?.key || 'all')}
                  />
                </div>
                {isAdmin && unassignedCount > 0 && (
                  <div className="pnid-fm-banner pnid-fm-banner--warning">
                    ⚠️ {unassignedCount} document{unassignedCount > 1 ? 's' : ''} unassigned
                  </div>
                )}
                {archiveError && (
                  <div className="pnid-fm-banner pnid-fm-banner--error">{archiveError}</div>
                )}

              {filteredDocuments.length === 0 ? (
                <div className="pnid-fm-empty">
                  {scopedDocuments.length === 0
                    ? 'No documents uploaded yet'
                    : 'No documents match your filters'}
                </div>
              ) : (
                /* A table, not cards. Every document carries the same seven
                   facts, and a card repeated 869 times is a table that has
                   forgotten it is one — the columns never line up, so nothing
                   can be compared down the page. */
                <DataTable
                  columns={fileColumns}
                  rows={fileRows}
                  rowKey="id"
                  page={filePage}
                  rowsPerPage={fileRowsPerPage}
                  total={filteredDocuments.length}
                  onPageChange={setFilePage}
                  onRowsPerPageChange={(value: number) => {
                    setFileRowsPerPage(value);
                    setFilePage(0);
                  }}
                  boldColumns={['name']}
                />
              )}
              </div>
            </div>
          )}

          {resolvedTab === 'background' && (
            <div className="pnid-fm-list pnid-fm-list--gap-lg">
              <div className="pnid-fm-job-head">
                <div className="pnid-fm-job-head-title">Active Background Jobs</div>
                <div className="pnid-fm-job-head-actions">
                  <button
                    className="pnid-btn pnid-btn--outlined pnid-btn--sm"
                    onClick={() => loadBackgroundJobs()}
                    disabled={backgroundLoading}
                  >
                    Refresh
                  </button>
                  <button
                    className="pnid-btn pnid-btn--outlined pnid-btn--sm"
                    onClick={() => handlePollBackgroundJobs()}
                    disabled={backgroundLoading}
                  >
                    Poll All
                  </button>
                </div>
              </div>

              {backgroundError && (
                <div className="pnid-fm-banner pnid-fm-banner--error">{backgroundError}</div>
              )}

              {backgroundLoading && (
                <div className="pnid-fm-loading">Loading background jobs…</div>
              )}

              {!backgroundLoading && backgroundJobs.length === 0 && (
                <div className="pnid-fm-empty">No active background jobs</div>
              )}

              {!backgroundLoading && backgroundJobs.map(job => (
                <div key={job.job_id} className="pnid-fm-row">
                  <div className="pnid-fm-row-main">
                    <div className="pnid-fm-job-title">
                      <span className="pnid-fm-job-id">{job.job_id}</span>
                      <span className="pnid-fm-job-status">{job.status}</span>
                    </div>
                    <div className="pnid-fm-job-meta">
                      <span>Doc: {job.doc_id}</span>
                      <span>Attempts: {job.attempts}</span>
                      <span>Created: {formatTimestamp(job.created_at)}</span>
                      <span>Last polled: {formatTimestamp(job.last_polled_at)}</span>
                    </div>
                    {job.error_message && (
                      <div className="pnid-fm-job-error">{job.error_message}</div>
                    )}
                  </div>
                  <div className="pnid-fm-row-actions pnid-fm-row-actions--stack">
                    <button
                      className="pnid-btn pnid-btn--contained pnid-btn--sm"
                      onClick={() => handleOpenDocument(job.doc_id)}
                    >
                      Open
                    </button>
                    {(job.status === 'processing' || job.status === 'pending') && (
                      <button
                        className="pnid-btn pnid-btn--outlined pnid-btn--sm"
                        onClick={() => handleCancelBackgroundJob(job.job_id)}
                      >
                        Cancel
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

        </div>
    </>
  );

  const viewer = (
    <DocumentViewer
      isOpen={viewerOpen}
      onClose={() => setViewerOpen(false)}
      docId={viewerDocId}
      docName={viewerDocName}
    />
  );

  if (presentation === 'page') {
    return (
      <>
        <PageHeader title="File Manager" />
        <div className="sme-page sme-page--wide sme-page--fill pnid-fm-page">{body}</div>
        {viewer}
      </>
    );
  }

  return (
    <>
      <PnidModal open title="P&ID File Manager" onClose={onClose} width={960}>
        {body}
      </PnidModal>
      {viewer}
    </>
  );
};


export default PnidFileManager;
